import { useMutation } from '@apollo/client';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import { useToast } from '@eluve/components';
import { useTranslation } from '@eluve/i18n';
import { DynamicArtifactTemplate } from '@eluve/llm-outputs';
import {
  useTenantIdFromParams,
  useUserIdFromSession,
} from '@eluve/session-helpers';
import { templateBuilderStore } from '@eluve/smart-blocks';

import { createOutputTemplateMutation } from './operations';
import { useIsOrganizationRoute } from './useIsOrganizationRoute';

export interface SchemaValidationResult {
  modelName: string;
  accepted: boolean;
  error?: string;
}

export const useSchemaValidation = () => {
  const toast = useToast();
  const apiClient = useApiClient();

  const [isValidating, setIsValidating] = useState(false);
  const [validationResults, setValidationResults] = useState<
    SchemaValidationResult[]
  >([]);
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false);

  const handleValidateSchema = useCallback(async () => {
    const { context } = templateBuilderStore.getSnapshot();
    const dynamicArtifact: DynamicArtifactTemplate = {
      name: context.name,
      description: context.description,
      blocks: context.blocks,
    };

    setIsValidationDialogOpen(true);
    setValidationResults([]);
    setIsValidating(true);

    try {
      const validation = await apiClient.adminLlm.validateOutputTemplateSchema({
        body: { outputTemplate: dynamicArtifact },
      });

      if (validation.status === 200) {
        setValidationResults(validation.body.results);
      } else {
        toast.error('Schema validation failed. Please try again.');
        setIsValidationDialogOpen(false);
      }
    } catch {
      toast.error('Schema validation request failed. Please try again.');
      setIsValidationDialogOpen(false);
    } finally {
      setIsValidating(false);
    }
  }, [apiClient, toast]);

  return {
    handleValidateSchema,
    isValidating,
    validationResults,
    isValidationDialogOpen,
    setIsValidationDialogOpen,
  };
};

export const useCreateTemplate = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const isOrganizationRoute = useIsOrganizationRoute();
  const tenantId = useTenantIdFromParams();
  const userId = useUserIdFromSession();

  const [createOutputTemplate] = useMutation(createOutputTemplateMutation, {
    onCompleted: ({ insertLlmOutputTemplatesOne: template }) => {
      toast.success(t('outputTemplates.common.templateCreated'));
      templateBuilderStore.send({ type: 'reset' });
      if (template) {
        navigate(`../${template.id}`);
      }
    },
    onError: () =>
      toast.error(t('outputTemplates.common.createTemplateFailed')),
  });

  const handleSubmit = useCallback(
    async (isDraft: boolean) => {
      const { context } = templateBuilderStore.getSnapshot();
      const dynamicArtifact: DynamicArtifactTemplate = {
        name: context.name,
        description: context.description,
        blocks: context.blocks,
      };

      await createOutputTemplate({
        variables: {
          input: {
            name: context.name,
            description: context.description,
            llmOutputType: context.outputType,
            ...(isOrganizationRoute ? { tenantId } : {}),
            variants: {
              data: [
                {
                  template: dynamicArtifact,
                  variantNotes: context.variantNotes,
                  isCurrentDefault: !isDraft,
                  isDraft: isDraft,
                },
              ],
            },
            ...(isOrganizationRoute && !isDraft
              ? {
                  users_llm_output_templates: {
                    data: [{ userId: userId!, tenantId: tenantId! }],
                  },
                }
              : {}),
          },
        },
      });
    },
    [createOutputTemplate, isOrganizationRoute, tenantId, userId],
  );

  return {
    handleSaveDraftTemplate: () => handleSubmit(true),
    handleSubmitTemplate: () => handleSubmit(false),
  };
};
